import { useState } from 'react'
import type { PnLWaterfall } from '@/types/api'
import {
  transformWaterfallData,
  transformWaterfallBucketsOnly,
  formatCurrency,
  formatPercentage,
  type WaterfallDataPoint,
} from '@/lib/chart-transformers'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { TrendingUp, TrendingDown, Eye, EyeOff } from 'lucide-react'

interface WaterfallChartProps {
  data: PnLWaterfall
  height?: number
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    payload: WaterfallDataPoint
  }>
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-semibold text-gray-900">{label}</p>
        <p className={`text-sm ${data.value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          P&L: {formatCurrency(data.value)}
        </p>
        {data.isTotal && (
          <Badge variant="secondary" className="mt-1">Total</Badge>
        )}
        {data.level === 1 && data.assetClass && (
          <Badge variant="outline" className="mt-1">
            {data.assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'}
          </Badge>
        )}
      </div>
    )
  }
  return null
}

export function WaterfallChart({ data, height = 400 }: WaterfallChartProps) {
  const [showPositions, setShowPositions] = useState(true)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  // Transform data based on view mode
  const chartData = showPositions
    ? transformWaterfallData(data)
    : transformWaterfallBucketsOnly(data)

  const portfolioTotal = data.portfolioTotal
  const isPositive = Number(portfolioTotal.percentagePnL) >= 0

  // Calculate bucket summaries
  const bucketSummaries = data.buckets.map(bucket => ({
    assetClass: bucket.assetClass,
    pnl: Number(bucket.absolutePnL),
    percentage: Number(bucket.percentagePnL),
    positionCount: bucket.positions.length,
  }))

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              P&L Waterfall
              {isPositive ? (
                <TrendingUp className="h-5 w-5 text-emerald-500" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-500" />
              )}
            </CardTitle>
            <CardDescription>
              Hierarchical breakdown: Portfolio → Asset Classes → Positions
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-positions"
                checked={showPositions}
                onCheckedChange={setShowPositions}
              />
              <Label htmlFor="show-positions" className="text-sm text-gray-600">
                {showPositions ? (
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> Positions</span>
                ) : (
                  <span className="flex items-center gap-1"><EyeOff className="h-3 w-3" /> Positions</span>
                )}
              </Label>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
              onMouseMove={(_, index) => setHoveredIndex(index as number)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis
                tickFormatter={(value) => formatCurrency(Number(value))}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />

              {/* Invisible base bars */}
              <Bar
                dataKey="invisible"
                stackId="a"
                fill="transparent"
                isAnimationActive={false}
              />

              {/* Visible P&L bars */}
              <Bar
                dataKey="visible"
                stackId="a"
                isAnimationActive={true}
                animationDuration={800}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.fill || '#6b7280'}
                    stroke={hoveredIndex === index ? '#1f2937' : 'transparent'}
                    strokeWidth={hoveredIndex === index ? 2 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>

      <CardFooter className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {formatCurrency(Number(portfolioTotal.absolutePnL))}
          </Badge>
          <span className="text-gray-600">Total P&L</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`font-mono ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatPercentage(Number(portfolioTotal.percentagePnL))}
          </Badge>
          <span className="text-gray-600">Return</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            {formatCurrency(Number(portfolioTotal.startingNAV))}
          </Badge>
          <span className="text-gray-600">Start NAV</span>
        </div>

        {/* Bucket breakdown */}
        {bucketSummaries.map((bucket) => (
          <div key={bucket.assetClass} className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={`font-mono ${bucket.pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
            >
              {bucket.assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'}: {formatCurrency(bucket.pnl)}
            </Badge>
            <span className="text-gray-500 text-xs">
              ({bucket.positionCount} positions)
            </span>
          </div>
        ))}
      </CardFooter>
    </Card>
  )
}

interface EmptyWaterfallChartProps {
  height?: number
}

export function EmptyWaterfallChart({ height = 400 }: EmptyWaterfallChartProps) {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg">P&L Waterfall</CardTitle>
        <CardDescription>
          Hierarchical breakdown: Portfolio → Asset Classes → Positions
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          style={{ height }}
          className="flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200"
        >
          <TrendingUp className="h-12 w-12 text-gray-300 mb-3" />
          <p className="text-gray-500 text-center">
            No P&L data available
          </p>
          <p className="text-gray-400 text-sm text-center mt-1">
            Run a scenario to see results
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
